import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { readData } from '@/firebase/database';

interface Service {
  title: string;
  description: string;
  icon: string;
  image?: string;
}

const Services = () => {
  const [services, setServices] = useState<Service[]>([]);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    readData('services').then((data) => {
      if (data) setServices(Object.values(data) as Service[]);
    });
  }, []);

  return (
    <section id="services" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-10 sm:mb-16">
          <p className="section-tag text-xs">What I offer</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            My <span className="gradient-text">Services</span>
          </h2>
          <div className="w-20 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {services.map((service, i) => (
            <motion.div key={service.title} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }} className="glass neon-border rounded-xl sm:rounded-2xl overflow-hidden card-hover group relative">
              {service.image && (
                <div className="h-24 sm:h-32 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
              )}
              <div className="p-3 sm:p-6">
                <div className="absolute inset-0 metallic-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <div className="text-2xl sm:text-4xl mb-2 sm:mb-4">{service.icon}</div>
                <h3 className="font-display text-sm sm:text-xl font-bold mb-1.5 sm:mb-3 group-hover:text-primary transition-colors duration-300">{service.title}</h3>
                <p className="text-muted-foreground text-[10px] sm:text-sm leading-relaxed">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
